import React, { useEffect, useState } from 'react'
import { isNil } from 'lodash'
import { Alert } from '@surecompanies/core_components'
import { Tooltip } from 'antd'
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined'
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined'
import CloseOutlined from '@ant-design/icons/CloseOutlined'
import DownloadOutlined from '@ant-design/icons/DownloadOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import SendOutlined from '@ant-design/icons/SendOutlined'
import SyncOutlined from '@ant-design/icons/SyncOutlined'
import WarningOutlined from '@ant-design/icons/WarningOutlined'
import { InvoiceUploadSection } from './InvoiceUploadSection'
import { PeriodLabel } from './InvoiceUploadSection.styles'
import { InvoiceDeliveryTableWithStripePayment } from './InvoiceDeliveryTableWithStripePayment'
import { InvoiceDeliveryDrawer } from './InvoiceDeliveryDrawer'
import { ConfirmDrawModal } from './ConfirmDrawModal'
import { ResolveUnmatchedModal } from './ResolveUnmatchedModal'
import { ReviewBeforeSendModal } from './ReviewBeforeSendModal'
import useInvoiceDelivery from './useInvoiceDelivery'
import { useCanDownloadFromInvoiceManagement } from './useCanDownloadFromInvoiceManagement'
import { downloadPaymentReportCsv } from './PaymentReportCsv'
import {
  SectionActions,
  SectionHeader,
  SectionTitle,
  DownloadPeriodButton,
  ResyncButton,
  StatCard,
  StatIcon,
  StatLabel,
  StatValue,
  StatsRow,
  BannerDescription,
  BannerHeader,
  BannerTextStack,
  BannerTitle,
  UnmatchedBanner,
  UnmatchedBannerActions,
  UnmatchedBannerCopy,
  UnmatchedBannerDismissButton,
  UnmatchedBannerPrimaryButton,
  WarningBanner,
  WarningBannerActions,
  WarningBannerCopy,
  WarningBannerDismissButton,
  WarningBannerPrimaryButton,
} from './BulkInvoiceUpload.styles'

/*
  Clone of `DeliveryModeContent` from BulkInvoiceUpload.tsx, reached at
  /finance/invoice-management/delivery-and-payment. Identical to the delivery
  view today; the Stripe payment pieces (EMS-10148) get built on top of this
  copy so the existing delivery view stays untouched.
*/

/*
  core.scss gives html `overflow-y: auto`, so antd's drawer and modal scroll
  lock (overflow-y: hidden on body) clips body at its 100% height instead of
  reaching the viewport, and the page jumps back to the top. Clearing it while
  this view is mounted lets the lock freeze the page where it is.
*/
const useKeepScrollUnderOverlays = () => {
  useEffect(() => {
    const html = document.documentElement
    const previous = html.style.overflowY
    html.style.overflowY = 'visible'
    return () => {
      html.style.overflowY = previous
    }
  }, [])
}

const DeliveryModeContentWithStripePayment: React.FC = () => {
  const {
    billingMonth,
    billingYear,
    billingPeriodStart,
    billingPeriodLabel,
    handleBillingMonthChange,
    handleBillingYearChange,

    deliveries,
    monthDeliveries,
    totalDeliveries,
    getTemplateRowInfo,
    isLoading,
    isError,
    stats,

    search,
    setSearch,
    statusFilter,
    handleStatusFilter,
    templateFilter,
    setTemplateFilter,
    allowedTemplates,
    isBannerVisible,
    handleDismissBanner,
    isWarningBannerVisible,
    handleDismissWarningBanner,
    showArchived,
    handleToggleArchived,

    resolveModalOpen,
    handleOpenResolveModal,
    handleOpenFirstWithWarnings,
    handleCloseResolveModal,

    isMutating,
    sendingIds,
    isBulkSending,
    isBulkSyncing,
    handleSend,
    handleBulkSync,
    handleBulkSend,
    handleDelete,
    handleSetArchived,
    handleUploadComplete,

    isDownloadingArchive,
    downloadedBytesLabel,
    handleDownloadSelected,
    handleDownloadMonth,

    selectedRowKeys,
    selectedSendableCount,
    handleSelectedRowKeysChange,

    drawerDelivery,
    currentDeliveryIndex,
    totalInDrawerList,
    canNavigatePrev,
    canNavigateNext,
    handleRowClick,
    handleDrawerClose,
    handleDrawerNavigate,
    reviewModal,
    handleCloseReview,
    handleReviewSent,
  } = useInvoiceDelivery()
  useKeepScrollUnderOverlays()
  const canDownloadInvoices = useCanDownloadFromInvoiceManagement()

  const [drawDelivery, setDrawDelivery] = useState<
    (typeof deliveries)[number] | null
  >(null)
  const handleDraw = (id: string) => {
    setDrawDelivery(deliveries.find((d) => d.id === id) ?? null)
  }
  const handleCloseDraw = () => setDrawDelivery(null)

  const handleDownloadPaymentReport = () =>
    downloadPaymentReportCsv(deliveries, billingPeriodStart)

  return (
    <>
      <InvoiceUploadSection
        billingMonth={billingMonth}
        billingYear={billingYear}
        onBillingMonthChange={handleBillingMonthChange}
        onBillingYearChange={handleBillingYearChange}
        onUploadComplete={handleUploadComplete}
      />

      {isBannerVisible && (
        <UnmatchedBanner role="alert">
          <UnmatchedBannerCopy>
            <BannerTextStack>
              <BannerHeader>
                <WarningOutlined aria-hidden />
                <BannerTitle>
                  {stats.unmatched === 1
                    ? '1 unmatched invoice'
                    : `${stats.unmatched} unmatched invoices`}
                </BannerTitle>
              </BannerHeader>
              <BannerDescription>
                Assign a company before sending.
              </BannerDescription>
            </BannerTextStack>
          </UnmatchedBannerCopy>
          <UnmatchedBannerActions>
            <UnmatchedBannerPrimaryButton
              type="button"
              onClick={handleOpenResolveModal}
            >
              Resolve unmatched
            </UnmatchedBannerPrimaryButton>
            <UnmatchedBannerDismissButton
              type="button"
              aria-label="Dismiss unmatched banner"
              onClick={handleDismissBanner}
            >
              <CloseOutlined />
            </UnmatchedBannerDismissButton>
          </UnmatchedBannerActions>
        </UnmatchedBanner>
      )}

      {isWarningBannerVisible && (
        <WarningBanner role="alert">
          <WarningBannerCopy>
            <BannerTextStack>
              <BannerHeader>
                <WarningOutlined aria-hidden />
                <BannerTitle>
                  {stats.withWarnings === 1
                    ? '1 invoice has unreadable cells'
                    : `${stats.withWarnings} invoices have unreadable cells`}
                </BannerTitle>
              </BannerHeader>
              <BannerDescription>
                Fix the values before sending or the email will go out with the
                wrong amount.
              </BannerDescription>
            </BannerTextStack>
          </WarningBannerCopy>
          <WarningBannerActions>
            <WarningBannerPrimaryButton
              type="button"
              onClick={handleOpenFirstWithWarnings}
              disabled={stats.withWarnings === 0}
            >
              Resolve issues
            </WarningBannerPrimaryButton>
            <WarningBannerDismissButton
              type="button"
              aria-label="Dismiss warning banner"
              onClick={handleDismissWarningBanner}
            >
              <CloseOutlined />
            </WarningBannerDismissButton>
          </WarningBannerActions>
        </WarningBanner>
      )}

      <StatsRow>
        <StatCard>
          <div>
            <StatLabel>Total</StatLabel>
            <StatValue>{stats.total}</StatValue>
          </div>
          <StatIcon>
            <FileTextOutlined />
          </StatIcon>
        </StatCard>

        <StatCard>
          <div>
            <StatLabel>Draft</StatLabel>
            <StatValue>{stats.draft}</StatValue>
          </div>
          <StatIcon>
            <ClockCircleOutlined />
          </StatIcon>
        </StatCard>

        <StatCard>
          <div>
            <StatLabel>Sending</StatLabel>
            <StatValue>{stats.sending}</StatValue>
          </div>
          <StatIcon>
            <SendOutlined />
          </StatIcon>
        </StatCard>

        <StatCard>
          <div>
            <StatLabel>Delivered</StatLabel>
            <StatValue>{stats.delivered}</StatValue>
          </div>
          <StatIcon>
            <CheckCircleOutlined />
          </StatIcon>
        </StatCard>
      </StatsRow>

      {isError && (
        <Alert
          type="error"
          message="Failed to load deliveries. Please refresh the page."
          showIcon
        />
      )}

      <SectionHeader>
        <SectionTitle>Invoice deliveries</SectionTitle>
        <SectionActions>
          {stats.sending > 0 && (
            <ResyncButton
              onClick={handleBulkSync}
              disabled={isMutating || isBulkSyncing}
            >
              <SyncOutlined spin={isBulkSyncing} />
              Resync stuck deliveries
            </ResyncButton>
          )}
          {/*
            Period-scoped, so it belongs with the section title rather than the
            table's filter row — it deliberately ignores the status filter,
            search text and archive toggle.
          */}
          <PeriodLabel>{billingPeriodLabel}</PeriodLabel>
          {canDownloadInvoices && (
            <Tooltip
              title={`Download all ${billingPeriodLabel} invoices as a ZIP. Ignores the filters below.`}
            >
              <DownloadPeriodButton
                onClick={handleDownloadMonth}
                disabled={isDownloadingArchive || totalDeliveries === 0}
              >
                {isDownloadingArchive ? (
                  <LoadingOutlined />
                ) : (
                  <DownloadOutlined />
                )}
                {/*
                  While in flight, show bytes received instead of the static
                  label — a big archive can take a while, and a bare spinner
                  gives no sign it is still making progress.
                */}
                {isDownloadingArchive
                  ? `Downloading… ${downloadedBytesLabel ?? ''}`
                  : 'Invoices'}
              </DownloadPeriodButton>
            </Tooltip>
          )}
          {/*
            Reads the rows already loaded for the table below, so unlike the
            ZIP download above, this respects the current status/search/archive
            filters rather than always covering the whole billing period.
          */}
          <Tooltip title="Download a CSV summary of the invoices below: one row per company total, followed by one row per Stripe customer draw attempt.">
            <DownloadPeriodButton
              onClick={handleDownloadPaymentReport}
              disabled={deliveries.length === 0}
            >
              <DownloadOutlined />
              Payment report
            </DownloadPeriodButton>
          </Tooltip>
        </SectionActions>
      </SectionHeader>

      <InvoiceDeliveryTableWithStripePayment
        deliveries={deliveries}
        monthDeliveries={monthDeliveries}
        total={totalDeliveries}
        isLoading={isLoading}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilter}
        templateFilter={templateFilter}
        templateOptions={allowedTemplates}
        onTemplateFilterChange={setTemplateFilter}
        showArchived={showArchived}
        onToggleArchived={handleToggleArchived}
        onSend={handleSend}
        onBulkSend={handleBulkSend}
        onDelete={handleDelete}
        onDraw={handleDraw}
        onSetArchived={handleSetArchived}
        onRowClick={handleRowClick}
        selectedRowKeys={selectedRowKeys}
        selectedSendableCount={selectedSendableCount}
        onSelectedRowKeysChange={handleSelectedRowKeysChange}
        isMutating={isMutating}
        sendingIds={sendingIds}
        isBulkSending={isBulkSending}
        canDownloadInvoices={canDownloadInvoices}
        onDownloadSelected={handleDownloadSelected}
        isDownloadingArchive={isDownloadingArchive}
        downloadedBytesLabel={downloadedBytesLabel}
        getTemplateRowInfo={getTemplateRowInfo}
      />

      <InvoiceDeliveryDrawer
        delivery={drawerDelivery}
        monthDeliveries={monthDeliveries}
        open={!isNil(drawerDelivery)}
        onClose={handleDrawerClose}
        onSetArchived={handleSetArchived}
        onDelete={handleDelete}
        currentIndex={currentDeliveryIndex}
        totalInList={totalInDrawerList}
        canNavigatePrev={canNavigatePrev}
        canNavigateNext={canNavigateNext}
        onNavigate={handleDrawerNavigate}
      />

      <ConfirmDrawModal
        delivery={drawDelivery}
        open={!isNil(drawDelivery)}
        onClose={handleCloseDraw}
      />

      <ResolveUnmatchedModal
        open={resolveModalOpen}
        billingPeriodStart={billingPeriodStart}
        onClose={handleCloseResolveModal}
      />

      {reviewModal && (
        <ReviewBeforeSendModal
          open
          scope={reviewModal.scope}
          deliveryIds={reviewModal.ids}
          monthDeliveries={monthDeliveries}
          onClose={handleCloseReview}
          onSent={handleReviewSent}
        />
      )}
    </>
  )
}

export { DeliveryModeContentWithStripePayment }
